/**
 * useSubtitleTracks — fetches VTT text for subtitle items via @subtitle-content.
 * @module components/manage/VideoJS/useSubtitleTracks
 */
import { useEffect, useRef, useState } from 'react';
import Api from '@plone/volto/helpers/Api/Api';
import { usePathKey, withPathKeyParam } from 'volto-base-addon/helpers/pathKey';

/**
 * @param {Array} subtitleItems - From getVideoPlayerData: [[{ '@id', label }], ...]
 * @returns {{ tracks: Array<{ label: string, vttText: string }>, isLoading: boolean }}
 */
export const useSubtitleTracks = (subtitleItems) => {
  const [tracks, setTracks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const pathKey = usePathKey();

  // Signature to detect when subtitle items change (by their @id paths)
  const signature = Array.isArray(subtitleItems)
    ? subtitleItems.map((s) => s[0]?.['@id'] || '').join('|')
    : '';
  const prevSignatureRef = useRef('');

  useEffect(() => {
    if (!signature || signature === prevSignatureRef.current) {
      if (!signature) {
        setTracks([]);
      }
      return;
    }
    prevSignatureRef.current = signature;

    const items = Array.isArray(subtitleItems) ? subtitleItems : [];
    if (items.length === 0) {
      setTracks([]);
      return;
    }

    const controller = new AbortController();
    const api = new Api();
    let cancelled = false;

    setIsLoading(true);

    const fetchAll = async () => {
      const settled = await Promise.all(
        items.map(async (item) => {
          const contentPath = item[0]?.['@id'];
          const label = item[0]?.label || item[0]?.title || '';
          if (!contentPath) return null;

          try {
            const resp = await api.get(
              withPathKeyParam(`${contentPath}/@subtitle-content`, pathKey),
              { signal: controller.signal },
            );
            return resp?.content ? { label, vttText: resp.content } : null;
          } catch (err) {
            if (err.name !== 'AbortError') {
              // eslint-disable-next-line no-console
              console.warn('Failed to fetch subtitle track:', contentPath, err);
            }
            return null;
          }
        }),
      );
      if (!cancelled) {
        setTracks(settled.filter(Boolean));
        setIsLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return { tracks, isLoading };
};

export default useSubtitleTracks;
